import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import {
  APP_THEME_MODE,
  AppThemeMode,
  THEME_STORAGE_KEY,
  createAppTheme,
} from '.';

interface AppThemeContextValue {
  mode: AppThemeMode;
  theme: ReturnType<typeof createAppTheme>;
  toggleTheme: () => void;
}

const getStoredTheme = (): AppThemeMode => {
  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

    return storedTheme === APP_THEME_MODE.DARK
      ? APP_THEME_MODE.DARK
      : APP_THEME_MODE.LIGHT;
  } catch {
    return APP_THEME_MODE.LIGHT;
  }
};

const AppThemeContext = createContext<AppThemeContextValue>({
  mode: APP_THEME_MODE.LIGHT,
  theme: createAppTheme(APP_THEME_MODE.LIGHT),
  toggleTheme: () => undefined,
});

export const AppThemeProvider = ({ children }: PropsWithChildren) => {
  const [mode, setMode] = useState<AppThemeMode>(getStoredTheme);

  const toggleTheme = useCallback(() => {
    setMode((currentMode) =>
      currentMode === APP_THEME_MODE.LIGHT
        ? APP_THEME_MODE.DARK
        : APP_THEME_MODE.LIGHT,
    );
  }, []);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = mode;

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // The selected theme remains active for the current session.
    }
  }, [mode]);

  const value = useMemo(
    () => ({ mode, theme: createAppTheme(mode), toggleTheme }),
    [mode, toggleTheme],
  );

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  );
};

export const useAppTheme = () => useContext(AppThemeContext);
